import { ComponentProps, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { H2 } from "../typography";

import BackButton from "./BackBtn";

import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Side } from "@/components/ui/customChervonIcon";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDataTableSlotContext } from "@/context/DataTableSlot";
import { cn } from "@/lib/utils";

export type tabs = {
  id: number;
  name: string;
  value: string;
};

export type BtnProps = ComponentProps<typeof ButtonWithIcon> & {
  btnName: string;
  showBtn: boolean;
  handleBtnClick?: () => void;
};

export type HeaderProps = {
  hide?: {
    functions?: boolean;
    filters?: boolean;
  };
  title?: string;
  btnProps?: BtnProps;
  link?: string;
  tabs?: tabs[];
  activeTab?: string;
  tabClick?: (tab: string) => void;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  backBtn?: () => void;
  className?: string;
  isLoading?: boolean;
  allowFilterReset?: boolean;
  custom?: {
    position: Side;
    node: ReactNode;
  };
} & React.HTMLAttributes<HTMLElement>;

const CustomHeader: React.FC<HeaderProps> = ({
  title,
  link,
  tabs,
  activeTab,
  setActiveTab,
  tabClick,
  className,
  backBtn,
  isLoading,
  custom,
  btnProps,
  allowFilterReset = true,
  hide = {
    functions: false,
    filters: false,
  },
}) => {
  const { btnName, showBtn, handleBtnClick, ...restBtnProps } = btnProps || {};
  const navigate = useNavigate();
  // const [_searchParam, setSearchParam] = useSearchParams();
  const { filters, functions, resetPagination, resetFilter, resetSearch } =
    useDataTableSlotContext();

  const handleClick = (): void => {
    if (handleBtnClick) {
      handleBtnClick();
    } else if (link) {
      navigate(link);
    }
  };

  const handleTabClick = (tab: string): void => {
    if (activeTab === tab) {
      return;
    }

    if (setActiveTab) {
      if (resetSearch) {
        resetSearch();
      }
      setActiveTab(tab);
      // reset pagination and filter on tab click
      resetPagination?.();
      if (allowFilterReset) {
        resetFilter?.();
      }
    }
    tabClick?.(tab);
  };

  return (
    <section className={cn("mt-4", className)}>
      <div className='flex flex-col justify-between w-full mb-2 md:flex-row '>
        {title && (
          <div className='flex items-center gap-2 max-w-[calc(100%-15rem)]'>
            {backBtn && <BackButton backBtn={backBtn} />}
            <H2 className='truncate'>{title}</H2>
          </div>
        )}
        <div className='flex items-center w-full gap-2 ml-auto md:w-auto'>
          {custom?.position === "left" && custom.node && custom.node}
          {!hide?.functions && functions ? functions : null}
          {custom?.position === "right" && custom.node && custom.node}
          {showBtn && (
            <ButtonWithIcon
              isLoading={isLoading}
              icon={btnProps?.icon}
              variant={btnProps?.variant ?? "default"}
              className={cn(btnProps?.className)}
              onClick={handleClick}
              {...restBtnProps}
            >
              <p>{btnName}</p>
            </ButtonWithIcon>
          )}
        </div>
      </div>

      <div className='flex items-center justify-between'>
        {tabs && activeTab && setActiveTab && (
          <Tabs defaultValue={activeTab}>
            <TabsList className={cn("flex w-fit border p-2 my-2")}>
              {tabs.map((tab) => {
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.value}
                    className={cn("text-xs")}
                    onClick={() => handleTabClick(tab?.value)}
                  >
                    {tab?.name}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        )}
        {!hide?.filters && filters && filters}
      </div>
    </section>
  );
};

export default CustomHeader;
